import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Checkbox from 'components/Checkbox';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { setFormData } from 'modules/forms/actions/form.actions';
import { acuteSoreThroatRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';

const FifthAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      additionalSymptoms: stateForm.additionalSymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const additionalSymptoms = watch('additionalSymptoms') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    const status = stateForm.status || EAppointmentFormStatus.COMPLETED;
    dispatch(
      setFormData({
        ...values,
        status:
          values.additionalSymptoms?.includes('6') || (values.additionalSymptoms?.length || 0) < 4
            ? EAppointmentFormStatus.NEEDS_REVIEW
            : status,
      }),
    );
    history.push({
      pathname: acuteSoreThroatRoutes.photoUpload,
      search: history.location.search,
    });
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'additionalSymptoms',
      e.target.checked
        ? [...additionalSymptoms, name].filter(item => item !== '6')
        : additionalSymptoms.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={7} maxSteps={8} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Which of the following symptoms is the patient experiencing (select all that apply):"
              >
                <Controller
                  name="additionalSymptoms"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={additionalSymptoms?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="High temperature over 37.9℃ or above"
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={additionalSymptoms?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="Any pus, which is a thick, yellowish or greenish fluid at the back of their throat"
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={additionalSymptoms?.includes('3')}
                        onChange={e => onChangeValue('3', e)}
                        label="The sore throat started in the last 3 days and is the first time the patient is seeking assistance for it."
                      />
                      <Checkbox
                        id="4"
                        name="4"
                        checked={additionalSymptoms?.includes('4')}
                        onChange={e => onChangeValue('4', e)}
                        label="Severely inflamed tonsils (if the patient still has their tonsils)"
                      />
                      <Checkbox
                        id="5"
                        name="5"
                        checked={additionalSymptoms?.includes('5')}
                        onChange={e => onChangeValue('5', e)}
                        label="The patient is not coughing or experiencing common Cold-like symptoms"
                      />
                      <Checkbox
                        id="6"
                        name="6"
                        checked={additionalSymptoms?.includes('6')}
                        onChange={e =>
                          setValue('additionalSymptoms', e.target.checked ? ['6'] : [])
                        }
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!additionalSymptoms?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default FifthAssessment;
